import type { Editor } from '@tiptap/core';
import { onBeforeUnmount, readonly, ref, watch } from 'vue';

/**
 * Tracks which paper IDs are currently cited in the Tiptap document.
 * Returns:
 * - `citedIds`   — reactive readonly Set that updates whenever the document changes.
 * - `citedOrder` — reactive readonly array of paperIds in order of first appearance.
 */
export function useCitedReferences(editorRef: {
    value: Editor | null | undefined;
}) {
    const citedIds = ref<Set<string>>(new Set());
    const citedOrder = ref<string[]>([]);

    let debounce: ReturnType<typeof setTimeout>;

    function collect(editor: Editor) {
        const ids = new Set<string>();
        const order: string[] = [];
        editor.state.doc.descendants((node) => {
            if (node.type.name === 'citation' && node.attrs.paperId) {
                const id = node.attrs.paperId as string;
                ids.add(id);

                if (!order.includes(id)) {
                    order.push(id);
                }
            }

            return true;
        });
        citedIds.value = ids;
        citedOrder.value = order;
    }

    function onUpdate() {
        clearTimeout(debounce);
        debounce = setTimeout(() => {
            if (editorRef.value) {
                collect(editorRef.value);
            }
        }, 300);
    }

    // Watch for the editor instance to become available and register the listener.
    const stopWatch = watch(
        () => editorRef.value,
        (editor) => {
            if (!editor) {
                return;
            }

            // Collect immediately on first availability.
            collect(editor);
            editor.on('update', onUpdate);
        },
        { immediate: true },
    );

    onBeforeUnmount(() => {
        stopWatch();
        clearTimeout(debounce);

        if (editorRef.value) {
            editorRef.value.off('update', onUpdate);
        }
    });

    return {
        citedIds: readonly(citedIds),
        citedOrder: readonly(citedOrder),
    };
}
